import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import { commonStyles } from '~/styles/commonStyles';
import { ContentBlockBody } from './ContentBlockBody';
import { ContentBlockEmpty } from './ContentBlockEmpty';
import { ContentBlockFooter } from './ContentBlockFooter';
import { ContentBlockFormBody } from './ContentBlockFormBody';
import { ContentBlockIframe } from './ContentBlockIframe';
import { ContentBlockInfoList } from './ContentBlockInfoList';
import { ContentBlockInfoListItem } from './ContentBlockInfoListItem';
import { ContentBlockSuspense } from './ContentBlockSuspense';
import { ContentBlockTitle } from './ContentBlockTitle';

type IDraftOutlineSize = 'sm' | 'md' | 'lg';

export type IContentBlockProps = IContainerProps & {
  children: React.ReactNode;
  flat?: boolean;
  draft?: boolean;
  draftOutlineSize?: IDraftOutlineSize;
};

const DRAFT_BORDER_WIDTH: Record<IDraftOutlineSize, number> = {
  sm: 4,
  md: 6,
  lg: 10,
};

const styles = stylex.create({
  host: {
    borderRadius: shapeTokens.corner$sm,
    color: colorSchemeTokens.onSurface,
  },
  host$draft: {
    overflow: 'auto',
  },
  host$draft$sm: {
    margin: -DRAFT_BORDER_WIDTH.sm,
  },
  host$draft$md: {
    margin: -DRAFT_BORDER_WIDTH.md,
  },
  host$draft$lg: {
    margin: -DRAFT_BORDER_WIDTH.lg,
  },
  inner: {
    borderRadius: 'inherit',
    backgroundColor: colorSchemeTokens.surfaceContainer,
  },
  inner$border: {
    outlineWidth: '1px',
    outlineStyle: 'solid',
    outlineColor: colorSchemeTokens.outlineVariant,
  },
  inner$draft: {
    borderColor: colorSchemeTokens.outline,
  },
  inner$draft$sm: {
    margin: DRAFT_BORDER_WIDTH.sm,
  },
  inner$draft$md: {
    margin: DRAFT_BORDER_WIDTH.md,
  },
  inner$draft$lg: {
    margin: DRAFT_BORDER_WIDTH.lg,
  },
});

const ContentBlock: React.FC<IContentBlockProps> = ({
  sx,
  children,
  flat,
  draft,
  draftOutlineSize = 'md',
}) => (
  <div
    {...stylex.props(
      styles.host,
      draft && [
        styles.host$draft,
        commonStyles.draftBackground,
        styles[`host$draft$${draftOutlineSize}`],
      ],
      sx,
    )}
  >
    <div
      {...stylex.props(
        styles.inner,
        !flat && styles.inner$border,
        draft && [
          styles.inner$draft,
          styles[`inner$draft$${draftOutlineSize}`],
        ],
        sx,
      )}
    >
      {children}
    </div>
  </div>
);

const ContentBlockNamespace = Object.assign(ContentBlock, {
  Title: ContentBlockTitle,
  Empty: ContentBlockEmpty,
  Body: ContentBlockBody,
  Suspense: ContentBlockSuspense,
  FormBody: ContentBlockFormBody,
  Footer: ContentBlockFooter,
  Iframe: ContentBlockIframe,
  InfoList: ContentBlockInfoList,
  InfoListItem: ContentBlockInfoListItem,
});

export { ContentBlockNamespace as ContentBlock };
